import { apiClient } from './client';
import type { Role } from '../types/api.types';

/**
 * Vocabulario de permisos — espejo de `aws-serverless/src/models/rbac.model.ts`.
 *
 * F0.3: antes el frontend tenía su propio vocabulario (`view:finanzas`,
 * `edit:users`…) y el Sidebar una tercera tabla por rutas, así que backend y
 * frontend discrepaban sobre quién podía qué. Ahora el backend sirve la matriz
 * en `GET /api/permissions/matrix`; esto es el tipo y el fallback para mientras
 * la petición viaja.
 */
export type Permission =
  // Asistencia por rostro
  | 'attendance:read' | 'attendance:take' | 'attendance:enroll' | 'attendance:policy'
  | 'attendance:prebaja_decide' | 'attendance:delete_biometrics' | 'attendance:gate' | 'attendance:gate_authorize'
  | 'lotes:read' | 'lotes:write' | 'lotes:approve'
  // Fase 3 (registro incompleto) — vista de supervisión ADMIN/SUPERVISOR.
  | 'operations:supervise'
  | 'projects:read' | 'projects:write'
  | 'tasks:read' | 'tasks:write' | 'tasks:manage'
  | 'crop:read' | 'crop:write'
  | 'irrigation:read' | 'irrigation:write'
  | 'harvest:read' | 'harvest:write'
  | 'merma:read' | 'merma:write'
  | 'machinery:read' | 'machinery:write'
  | 'porcino:read' | 'porcino:write'
  | 'protocol:read' | 'protocol:write'
  | 'inventory:read' | 'inventory:write'
  | 'warehouse:transfer'
  | 'traceability:read' | 'traceability:write'
  | 'transport:read' | 'transport:write'
  | 'export:read' | 'export:write'
  | 'finance:read' | 'finance:write'
  | 'payroll:read' | 'payroll:write'
  | 'users:read' | 'users:write'
  // Gastos generales: aprobación (concedible individualmente al aprobar un registro).
  | 'calculators:read'
  | 'expenses:read'
  | 'expenses:approve'
  | 'ranchos:read' | 'ranchos:write'
  // F5 — dispositivos de telemetría (espejo de rbac.model.ts backend).
  | 'devices:read' | 'devices:write'
  | 'vocabulary:read' | 'vocabulary:write'
  | 'config:read' | 'config:write'
  | 'approvals:read' | 'approvals:write'
  | 'audit:read'
  | 'routines:read'
  | 'production:read' | 'production:write'
  | 'forecast:read'
  | 'anomalies:read' | 'anomalies:write'
  | 'compliance:read' | 'compliance:write'
  | 'sales:read' | 'sales:write'
  | 'dashboard:read' | 'reports:read' | 'reports:configure' | 'map:read' | 'chat:use'
  | 'admin:ranchos' | 'admin:rbac' | 'admin:system' | 'admin:bulk-import' | 'admin:super';

export interface RoleAdjustment { add: Permission[]; remove: Permission[] }

export interface PermissionsMatrix {
  permissions: Permission[];
  /** Permisos de cada rol en esta empresa (de fábrica más los ajustes de la empresa). */
  roles: Record<Role, Permission[]>;
  /** Matriz de fábrica, sin ajustes. */
  defaults?: Record<Role, Permission[]>;
  adjustments?: Record<string, RoleAdjustment>;
  /** Roles que no se pueden ajustar y permisos que ningún ajuste puede conceder. */
  nonAdjustableRoles?: string[];
  nonGrantable?: Permission[];
  /** Permisos efectivos de quien pregunta (roles + overrides). */
  effective: Permission[];
}

export const getPermissionsMatrix = async (): Promise<PermissionsMatrix> =>
  (await apiClient.get<PermissionsMatrix>('/api/permissions/matrix')).data;

/** Solo SUPER_ADMIN: guarda los ajustes de permisos por rol de la empresa. */
export async function updateRoleAdjustments(adjustments: Record<string, RoleAdjustment>): Promise<{ adjustments: Record<string, RoleAdjustment> }> {
  return (await apiClient.put('/api/permissions/role-adjustments', { adjustments })).data;
}

export interface UserPermissionOverride {
  phoneNumber: string;
  permissions: string[]; // permisos adicionales
}

export async function getUserPermissionOverrides(phoneNumber: string): Promise<UserPermissionOverride> {
  const res = await apiClient.get(`/api/permissions/overrides/${encodeURIComponent(phoneNumber)}`);
  return res.data;
}

export async function updateUserPermissionOverrides(
  phoneNumber: string,
  permissions: string[]
): Promise<UserPermissionOverride> {
  const res = await apiClient.put(`/api/permissions/overrides/${encodeURIComponent(phoneNumber)}`, {
    permissions,
  });
  return res.data;
}

export async function searchUserByPhone(query: string): Promise<any> {
  const res = await apiClient.get(`/api/users/search`, {
    params: { q: query }, // backend interprets as name or phoneNumber (case-insensitive)
  });
  return res.data;
}
